import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { RefreshService } from 'src/app/refresh.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ApiService } from 'src/app/services/api.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';

// Constants
import { Urls } from 'src/app/lib/urls';
import { TRANSACTION_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { BUYLOAD_TABLE_SUB_HEADERS } from 'src/app/lib/constants/transactions.constants';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
  selector: 'app-buy-load',
  templateUrl: './buy-load.component.html',
  styleUrls: ['./buy-load.component.scss'],
})
export class BuyLoadComponent implements OnInit, OnDestroy {
  showTable = true;
  private destroy$ = new Subject<void>();
  tabs = TRANSACTION_TAB_OPTIONS;
  public form: UntypedFormGroup;
  searchForm: UntypedFormGroup;
  limitForm: UntypedFormGroup;

  // Dropdown options
  itemTypeOptions = [
    { label: 'Smart',         value: '1' },
    { label: 'Globe',         value: '2' },
    { label: 'Sun',           value: '3' },
    { label: 'TNT',           value: '4' },
    { label: 'Smart Bro',     value: '5' },
    { label: 'Kuryente Load', value: '6' },
    { label: 'Cignal',        value: '7' },
    { label: 'DITO',          value: '8' },
  ];

  statusOptions = [
    { label: 'Success',  value: 'Success' },
    { label: 'Error',    value: 'Error' },
    { label: 'Waiting',  value: 'Waiting' },
    { label: 'Reversed', value: 'Reversed' },
  ];

  itemType: any = null;
  itemType_text: any = null;
  statusType: any = null;
  statusType_text: any = null;

  // Table
  tableSubHeader = BUYLOAD_TABLE_SUB_HEADERS;

  // Data
  itemLists: any[] = [];
  submitted = false;
  isLoading = false;
  fileName = '';

  // Filter fields — stores the submitted filter criteria
  filterFields: any = null;

  // Pagination
  size = 10;
  total_pages = 0;
  current_page = 1;
  pages_array: number[] = [];
  total = 0;

  // Search & Limit
  search_keyword = '';
  searchKey: any = null;
  limitKey: any = null;

  // Sorting
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Refund State ──────────────────────────────────────────────────────────
  /** Tracks which transactionId is currently being refunded to prevent double-clicks */
  refundingTransactionId: string | null = null;

  containsNumbers(value: string): boolean {
    return /\d/.test(value);
  }

  /**
   * Determines whether the Refund button for a row should be actionable.
   * Returns an object so the template can drive both [disabled] and [matTooltip] from one call.
   */
  getRefundButtonState(item: any): { disabled: boolean; tooltip: string } {
    if (!this.authService.isAdmin()) {
      return { disabled: true, tooltip: 'Account is not an Admin' };
    }
    if ((item.status ?? '').toLowerCase() !== 'waiting') {
      return { disabled: true, tooltip: 'Buyload already processed' };
    }
    if (this.refundingTransactionId === item.transactionId) {
      return { disabled: true, tooltip: 'Refund in progress…' };
    }
    return { disabled: false, tooltip: '' };
  }

  // ─── Resets ────────────────────────────────────────────────────────────────

  private resetForms(): void {
    this.form.reset({ search: '' });
    this.searchForm.reset({ search: '' });
    this.limitForm.reset({ search: '' });
  }

  private resetDropdowns(): void {
    this.itemType = null;
    this.itemType_text = null;
    this.statusType = null;
    this.statusType_text = null;
  }

  private resetData(): void {
    this.itemLists = null;
    this.submitted = false;
    this.isLoading = false;
    this.fileName = '';
  }

  private resetFilterFields(): void {
    this.filterFields = null;
  }

  private resetPagination(): void {
    this.size = 10;
    this.total_pages = 0;
    this.current_page = 1;
    this.pages_array = null;
    this.total = 0;
  }

  private resetSearch(): void {
    this.search_keyword = '';
    this.searchKey = '';
    this.limitKey = '';
  }

  private resetLocalStorage(): void {
    localStorage.setItem('itemLists', JSON.stringify(null));
  }

  private resetTableState(): void {
    this.resetForms();
    this.resetDropdowns();
    this.resetData();
    this.resetFilterFields();
    this.resetPagination();
    this.resetSearch();
    this.resetLocalStorage();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  constructor(
    private fb: UntypedFormBuilder,
    private request: RequestsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public apiService: ApiService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private refreshService: RefreshService,
    private popUpDialog: PopUpDialogService,
    private authService: AuthService,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on init:', err);
    });

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.form.reset({ search: '' });
      this.resetTableState();
      this.showTable = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on destroy:', err);
    });
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      transactionId: [null],
      firstName:     [null],
      middleName:    [null],
      lastName:      [null],
      phoneNumber:   [null],
      productId:     [null],
      dateFrom:      [null],
      dateTo:        [null],
    });

    // Search with debounce
    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.searchForm = this.fb.group({
      search: [''],
    });

    // Limit with debounce
    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.limitForm = this.fb.group({
      limit: [this.size],
    });
  }

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  submit(): void {
    if (!this.validateDates()) {
      return;
    }

    this.isLoading = true;
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    this.current_page = 1;

    const params = {
      transactionId: this.form.controls.transactionId.value,
      firstName:     this.form.controls.firstName.value,
      middleName:    this.form.controls.middleName.value,
      lastName:      this.form.controls.lastName.value,
      phoneNumber:   this.form.controls.phoneNumber.value,
      productId:     this.form.controls.productId.value,
      dateFrom:      this.form.controls.dateFrom.value,
      dateTo:        this.form.controls.dateTo.value,
      itemType:      this.itemType,
      status:        this.statusType,
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    this.filterFields = params;
    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getBuyloadTransactions(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.itemLists = response.result.data;
            this.submitted = true;

            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages,
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Buy Load Transactions',
          );
        },
        complete: () => {
          this.form.reset();
          this.itemType        = null;
          this.itemType_text   = null;
          this.statusType      = null;
          this.statusType_text = null;

          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        },
      });

    this.showTable = true;
  }

  get_filtered_list(): void {
    this.isLoading = true;

    const params = {
      ...this.filterFields,
      search: this.search_keyword,
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getBuyloadTransactions(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.itemLists = response.result.data;

            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages,
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Buy Load Transactions',
          );
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        },
      });
  }

  // ─── Refund ────────────────────────────────────────────────────────────────

  /**
   * Opens the confirmation dialog before refunding.
   * Guards: admin check + waiting status check are already enforced
   * by [disabled] on the button, but we double-check here for safety.
   */
  openRefundConfirmation(item: any): void {
    if (!this.authService.isAdmin() || (item.status ?? '').toLowerCase() !== 'waiting') {
      return;
    }

    this.popUpDialog.openInfoActionDialog(
      `
        Are you sure you want to refund transaction?<br><br>
        <strong>Note:</strong><br>
        Once Refunded, the action cannot be reversed and<br>
        amount will be refunded back to the user
      `,
      () => this.executeRefund(item),
    );
  }

  private executeRefund(item: any): void {
    // Lock this specific row immediately to prevent double-click
    this.refundingTransactionId = item.transactionId;

    this.spinner.show().catch((err) => {
      console.error('Spinner show error during refund:', err);
    });

    this.apiService.refundTransaction(item.transactionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            // Update the row status locally so the button disables immediately
            // without waiting for a full table refresh
            const row = this.itemLists.find(
              (i) => i.transactionId === item.transactionId,
            );
            if (row) {
              row.status = 'Reversed';
            }

            this.popUpDialog.openSuccessDialog(
              `
                <strong>Refund Successful</strong> <br><br> 
                Buyload reversed and amount refunded <br> back to the user
              `,
            );
          } else {
            if(response.status === 401) {
              this.popUpDialog.openErrorDialog(
                `
                  <strong>Unauthorized Access</strong> <br><br>
                  Make sure your account has admin permission to enable this feature
                `,
              );
              return;
            }
            this.popUpDialog.openErrorDialog(
              `
              Refund failed for transaction <strong>${item.transactionId}</strong>. <br><br>
              Please try again or contact the developer.
              `,
            );
          }
        },
        error: (error) => {
          this.popUpDialog.openErrorDialog(
            `
              An error occurred while processing the refund for transaction <strong>${item.transactionId}</strong>.<br><br>
              Please refresh or contact the developer if the issue persists.
            `,
          );
        },
        complete: () => {
          // Unlock the row regardless of outcome
          this.refundingTransactionId = null;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error after refund:', err);
          });
        },
      });
  }

  // ─── Dropdown Setters ──────────────────────────────────────────────────────

  setItemType(text: string, value: string): void {
    this.itemType      = value;
    this.itemType_text = text;
  }

  setStatusType(text: string, value: string): void {
    this.statusType      = value;
    this.statusType_text = text;
  }

  // ─── Table Controls Events ─────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList(): void {
    this.current_page   = 1;
    this.search_keyword = this.searchForm.value.search;
    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  validateDates(): boolean {
    const dateFrom = this.form.get('dateFrom').value;
    const dateTo   = this.form.get('dateTo').value;

    if (dateFrom && dateTo) {
      const fromDate = new Date(dateFrom);
      const toDate   = new Date(dateTo);

      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(0, 0, 0, 0);

      if (toDate < fromDate) {
        this.toastr.error('Date To cannot be earlier than Date From', 'Invalid Date Range');
        return false;
      }
    }
    return true;
  }

  // ─── Sorting & Pagination ──────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => {
      console.error('Spinner show sort error:', err);
    });

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type,
    );

    this.sort_column = column;
    this.sort_type   = type;

    if (this.submitted && this.filterFields) {
      this.get_filtered_list();
    }
  }

  setPage(num: number): void {
    this.current_page = num;
    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  async print(): Promise<void> {
    this.spinner.show().catch((error) => { console.log(error); });
    const current = new Date();
    this.filterFields = JSON.parse(localStorage.getItem('itemLists'));
    this.fileName = current.toLocaleDateString() + '-buy-load.xlsx';


    try {
      await this.request.postBlob(Urls.exportTransactionsBuyload, this.filterFields).then((report) => {
        const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = data;
        link.download = this.fileName;
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
      this.spinner.hide().catch((error) => { console.log(error); });
    } catch (error) {
      this.toastr.error(
        `Please refresh or contact the developer if the error still occurs`,
        'Something went wrong'
      );
      this.spinner.hide().catch((err) => { console.log(err); });
    }
  }
}
